import AdyenCheckout from '@adyen/adyen-web';
import clsx from 'clsx';
import { ADYEN_RESULT_CODES } from 'components/AdyenDropIn/AdyenResultCodes';
import { Spinner } from 'components/UI/Spinner/Spinner';
import React from 'react';
import { AdyenSession } from 'types/PaymentMethod';

import '@adyen/adyen-web/dist/adyen.css';
import classes from './AdyenDropIn.module.scss';

interface AdyenAddCardDropInProps {
    session: AdyenSession;
    className?: string;
    payButtonLabel?: string;
    onComplete: (success: boolean) => void;
    onError?: (message: string) => void;
}

const READY_WATCHDOG_MS = 8000;
const COMPLETION_DELAY_MS = 3000;

export const AdyenAddCardDropIn: React.FC<AdyenAddCardDropInProps> = ({
    session,
    className,
    payButtonLabel,
    onComplete,
    onError
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkoutInstanceRef = React.useRef<any>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dropInInstanceRef = React.useRef<any>(null);

    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        let watchdog: ReturnType<typeof setTimeout> | undefined;
        let cancelled = false;

        const createCheckout = async () => {
            if (dropInInstanceRef.current) {
                try {
                    dropInInstanceRef.current.unmount();
                } catch {}
                dropInInstanceRef.current = null;
            }

            if (
                checkoutInstanceRef.current &&
                typeof checkoutInstanceRef.current.destroy === 'function'
            ) {
                try {
                    checkoutInstanceRef.current.destroy();
                } catch {}
                checkoutInstanceRef.current = null;
            }

            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }

            setIsLoading(true);

            const configuration = {
                environment: `${process.env.REACT_APP_ADYEN_ENVIRONMENT}`,
                clientKey: `${process.env.REACT_APP_ADYEN_CLIENT_KEY}`,
                ...(payButtonLabel
                    ? {
                          locale: 'en-US',
                          translations: {
                              'en-US': {
                                  payButton: payButtonLabel,
                                  confirmPreauthorization: payButtonLabel
                              },
                              'en-GB': {
                                  payButton: payButtonLabel,
                                  confirmPreauthorization: payButtonLabel
                              },
                              'en': {
                                  payButton: payButtonLabel,
                                  confirmPreauthorization: payButtonLabel
                              }
                          }
                      }
                    : {}),
                session: {
                    id: session.id,
                    sessionData: session.sessionData
                },
                onPaymentCompleted: ({ resultCode }: { resultCode: string }) => {
                    const resultCodeInfo = ADYEN_RESULT_CODES.get(resultCode);

                    if (!resultCodeInfo || !resultCodeInfo.success) {
                        onError?.('An error has occurred while saving your card.');
                        return;
                    }

                    // Give the backend a moment to persist the new card via webhook
                    setTimeout(() => {
                        if (!cancelled) {
                            onComplete(true);
                        }
                    }, COMPLETION_DELAY_MS);
                },
                onError: (error: Error) => {
                    const message =
                        error.name === 'NETWORK_ERROR'
                            ? 'Network error!'
                            : 'An unexpected error has occurred.';
                    onError?.(message);
                },
                paymentMethodsConfiguration: {
                    card: {
                        hasHolderName: true,
                        holderNameRequired: false,
                        billingAddressRequired: false
                    }
                }
            };

            const checkout = await AdyenCheckout(configuration);
            if (cancelled) return;

            checkoutInstanceRef.current = checkout;

            if (containerRef.current) {
                const dropin = checkout
                    .create('dropin', {
                        showStoredPaymentMethods: false,
                        onReady: () => {
                            if (!cancelled) {
                                setIsLoading(false);
                            }
                        }
                    })
                    .mount(containerRef.current);

                dropInInstanceRef.current = dropin;

                watchdog = setTimeout(() => {
                    if (!cancelled) {
                        setIsLoading(false);
                    }
                }, READY_WATCHDOG_MS);
            }
        };

        createCheckout();

        return () => {
            cancelled = true;
            if (watchdog) clearTimeout(watchdog);

            if (dropInInstanceRef.current) {
                try {
                    dropInInstanceRef.current.unmount();
                } catch {}
            }
            if (
                checkoutInstanceRef.current &&
                typeof checkoutInstanceRef.current.destroy === 'function'
            ) {
                try {
                    checkoutInstanceRef.current.destroy();
                } catch {}
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.id, session.sessionData]);

    return (
        <div className={clsx(classes.container, className)}>
            {isLoading && (
                <div className="w-full flex justify-center">
                    <Spinner />
                </div>
            )}

            <div ref={containerRef} className={classes.dropIn} />
        </div>
    );
};
